import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import { matchesApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { StatusBadge, SkillTag } from '../components/common/Badges';
import ScoreRing from '../components/common/ScoreRing';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Sparkles,
  Loader2,
  User,
  Briefcase,
  MapPin,
  Clock,
  Mail,
  Phone,
} from 'lucide-react';
import { toast } from 'sonner';

export default function MatchDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMatch();
  }, [id]);

  const loadMatch = async () => {
    try {
      const response = await matchesApi.get(id);
      setMatch(response.data);
    } catch (error) {
      toast.error('Failed to load match');
      navigate('/ats/matches');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!match) return null;

  const { candidate, job } = match;

  const scoreBreakdown = [
    { label: 'Skills Match', score: match.skills_score, weight: '40%' },
    { label: 'Experience', score: match.experience_score, weight: '25%' },
    { label: 'Education', score: match.education_score, weight: '15%' },
    { label: 'Location', score: match.location_score, weight: '10%' },
    { label: 'Compensation', score: match.compensation_score, weight: '10%' },
  ];

  return (
    <div>
      <Header 
        title="Match Analysis" 
        subtitle={`${candidate?.name || 'Candidate'} → ${job?.job_title || 'Job'}`}
      >
        <Button variant="outline" onClick={() => navigate('/ats/matches')} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
      </Header>

      <div className="page-container">
        {/* Hero Score */}
        <Card className="mb-6" data-testid="score-hero">
          <CardContent className="p-8">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <ScoreRing score={match.overall_score} size="lg" />
              
              <div className="flex-1 text-center md:text-left">
                <h2 className="text-2xl font-bold text-slate-900">
                  {match.recommendation_level} Match
                </h2>
                <p className="text-slate-600 mt-2 max-w-xl">
                  {match.match_reasoning}
                </p>
              </div>

              <div className="flex flex-col gap-2">
                <Link to={`/ats/candidates/${match.candidate_id}`}>
                  <Button variant="outline" className="w-full" data-testid="view-candidate-btn">
                    <User className="w-4 h-4 mr-2" />
                    View Candidate
                  </Button>
                </Link>
                <Link to={`/ats/jobs/${match.job_id}`}>
                  <Button variant="outline" className="w-full" data-testid="view-job-btn">
                    <Briefcase className="w-4 h-4 mr-2" />
                    View Job
                  </Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Score Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <Card className="lg:col-span-2" data-testid="score-breakdown">
            <CardHeader>
              <CardTitle className="text-lg">Score Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {scoreBreakdown.map((item) => (
                  <div key={item.label}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-slate-600">{item.label}</span>
                      <span className="font-medium">
                        {item.score?.toFixed(0)}% 
                        <span className="text-slate-400 text-xs ml-1">({item.weight})</span>
                      </span>
                    </div>
                    <div className="progress-bar">
                      <div 
                        className={`progress-fill ${
                          item.score >= 85 ? 'bg-emerald-500' :
                          item.score >= 70 ? 'bg-blue-500' :
                          item.score >= 50 ? 'bg-amber-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${item.score}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Flags */}
          <Card data-testid="flags-card">
            <CardHeader>
              <CardTitle className="text-lg">Insights</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {match.green_flags?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-emerald-700 mb-2 flex items-center gap-1">
                    <Sparkles className="w-4 h-4" />
                    Strengths
                  </p>
                  <ul className="space-y-1">
                    {match.green_flags.map((flag, i) => (
                      <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                        {flag}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {match.red_flags?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-red-700 mb-2 flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" />
                    Concerns
                  </p>
                  <ul className="space-y-1">
                    {match.red_flags.map((flag, i) => (
                      <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                        <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                        {flag}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {!match.green_flags?.length && !match.red_flags?.length && (
                <p className="text-sm text-slate-500">No specific flags identified</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Skills Analysis */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <Card data-testid="matching-skills">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-500" />
                Matching Skills ({match.matching_keywords?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {match.matching_keywords?.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {match.matching_keywords.map((skill, i) => (
                    <SkillTag key={i} skill={skill} matched />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">No matching skills found</p>
              )}
            </CardContent>
          </Card>

          <Card data-testid="missing-skills">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <XCircle className="w-5 h-5 text-red-500" />
                Missing Skills ({match.missing_keywords?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {match.missing_keywords?.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {match.missing_keywords.map((skill, i) => (
                    <SkillTag key={i} skill={skill} missing />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">No critical skills missing</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Side by Side Comparison */}
        <Card data-testid="comparison">
          <CardHeader>
            <CardTitle className="text-lg">Side-by-Side Comparison</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Candidate */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">{candidate?.name}</h4>
                    <p className="text-sm text-slate-500">{candidate?.current_designation}</p>
                  </div>
                </div>
                
                <div className="space-y-2 text-sm">
                  {candidate?.email && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <Mail className="w-4 h-4" />
                      {candidate.email}
                    </div>
                  )}
                  {candidate?.current_location && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <MapPin className="w-4 h-4" />
                      {candidate.current_location}
                    </div>
                  )}
                  {candidate?.total_years_experience !== null && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <Clock className="w-4 h-4" />
                      {candidate.total_years_experience} years experience
                    </div>
                  )}
                </div>

                {candidate?.technical_skills?.length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs text-slate-500 mb-2">Skills</p>
                    <div className="flex flex-wrap gap-1">
                      {candidate.technical_skills.slice(0, 8).map((skill, i) => (
                        <span key={i} className="skill-tag text-xs">{skill}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Job */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                    <Briefcase className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">{job?.job_title}</h4>
                    <p className="text-sm text-slate-500">{job?.company_name}</p>
                  </div>
                </div>
                
                <div className="space-y-2 text-sm">
                  {job?.primary_location && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <MapPin className="w-4 h-4" />
                      {job.primary_location}
                    </div>
                  )}
                  {(job?.min_experience_years !== null || job?.max_experience_years !== null) && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <Clock className="w-4 h-4" />
                      {job.min_experience_years || 0}-{job.max_experience_years || '∞'} years required
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <StatusBadge status={job?.status} />
                  </div>
                </div>

                {job?.must_have_skills?.length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs text-slate-500 mb-2">Required Skills</p>
                    <div className="flex flex-wrap gap-1">
                      {job.must_have_skills.slice(0, 8).map((skill, i) => (
                        <span key={i} className="skill-tag text-xs">{skill}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
